import React, { useRef, useState } from 'react';
import { Film, X, Upload } from 'lucide-react';
import { apiClient, formatApiError } from '../context/AuthContext';
import toast from 'react-hot-toast';

const BACKEND = process.env.REACT_APP_BACKEND_URL || '';

function absolutize(url) {
  if (!url) return url;
  if (/^https?:\/\//i.test(url)) return url;
  return `${BACKEND}${url}`;
}

const MAX_VIDEOS = 5;

export default function VideoUploader({ videos = [], onChange }) {
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const remaining = MAX_VIDEOS - videos.length;
    if (remaining <= 0) {
      toast.error(`${MAX_VIDEOS} vidéos maximum`);
      e.target.value = '';
      return;
    }
    setUploading(true);
    try {
      const urls = [];
      for (const file of files.slice(0, remaining)) {
        if (!file.type.startsWith('video/')) {
          toast.error(`${file.name}: format vidéo invalide`);
          continue;
        }
        if (file.size > 50 * 1024 * 1024) {
          toast.error(`${file.name}: vidéo > 50 Mo`);
          continue;
        }
        const fd = new FormData();
        fd.append('file', file);
        const { data } = await apiClient.post('/upload/product-video', fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        if (data?.url) urls.push(data.url);
      }
      if (urls.length) {
        onChange([...videos, ...urls]);
        toast.success(`${urls.length} vidéo(s) ajoutée(s)`);
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const removeVideo = (idx) => {
    onChange(videos.filter((_, i) => i !== idx));
  };

  return (
    <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 space-y-3" data-testid="video-uploader">
      <div className="flex items-center justify-between">
        <label className="text-sm font-semibold text-gray-800 flex items-center gap-2">
          <Film size={15} /> Vidéos produit
          <span className="text-xs font-normal text-gray-400">({videos.length}/{MAX_VIDEOS})</span>
        </label>
        {videos.length < MAX_VIDEOS && (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            className="inline-flex items-center gap-2 bg-[#00aaff] hover:bg-[#0088dd] text-white text-xs font-bold px-3 py-2 rounded-lg disabled:opacity-60"
            data-testid="video-uploader-add"
          >
            {uploading ? (
              <>
                <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Upload…
              </>
            ) : (
              <>
                <Upload size={13} /> Ajouter une vidéo
              </>
            )}
          </button>
        )}
      </div>

      {videos.length === 0 ? (
        <p className="text-xs text-gray-400 italic">
          MP4, WebM ou MOV · 50 Mo max. Les vidéos apparaîtront dans la galerie produit.
        </p>
      ) : (
        <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3">
          {videos.map((url, idx) => (
            <div
              key={idx}
              className="relative group rounded-xl overflow-hidden bg-black ring-1 ring-gray-200"
              data-testid={`video-item-${idx}`}
            >
              <video
                src={absolutize(url)}
                className="w-full h-32 object-cover"
                muted
                playsInline
                preload="metadata"
                onMouseEnter={(e) => {
                  e.currentTarget.play().catch(() => {});
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.pause();
                  e.currentTarget.currentTime = 0;
                }}
              />
              <button
                type="button"
                onClick={() => removeVideo(idx)}
                className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/70 hover:bg-red-500 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                data-testid={`video-remove-${idx}`}
              >
                <X size={14} />
              </button>
              <div className="absolute bottom-2 left-2 bg-black/60 text-white text-[10px] px-2 py-0.5 rounded">
                Vidéo {idx + 1}
              </div>
            </div>
          ))}
        </div>
      )}

      <input
        ref={inputRef}
        type="file"
        accept="video/mp4,video/webm,video/quicktime,video/*"
        multiple
        onChange={handleFiles}
        className="hidden"
      />
    </div>
  );
}
